import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import { Button } from "components/Button"
import FatButton from "components/Button/FatButton/FatButton"
import { Buttons, Card } from "components/Card/Card"
import { FormPage } from "components/Form/Form"
import { Input } from "components/Input"
import { Keyboard, KeyboardReset } from "hocs/withKeyboard"
import PreviousButton from "components/Previous/PreviousButton"
import { Steps } from "components/Steps"
import { useStepper } from "components/Steps/hooks"
import actionsSurvey from "containers/Survey/services/actions"
import actionsUpload from "containers/Upload/services/actions"
import { PATH } from "containers/Survey/models"
import {
  CANCEL_PROFESSIONAL_TO_INSERT,
  CARERS_CLEAR_MESSAGE,
  GET_IDENTITY_FROM_RPPS_REQUEST,
} from "containers/InsertCarer/services/constants"
import { SURVEY_PAGE } from "core/constants"
import { cdn } from "core/cdn"
import { useLanguage } from "locales"
import { InsertCarerStore, AdminStore } from "types/store"
import { adminBillingRequest } from "containers/Admin/services/actions"
import { pharmacistRppsLengthValidator } from "lib/form"

const FINESS_KEY = "pharmacist_finess"

type Step = "rpps_form" | "rpps_confirm" | "finess"

const PharmacistInfo: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { languages } = useLanguage()
  const { current, insideStep, totalInsideSteps, totalSteps } = useStepper()

  const [step, setStep] = useState<Step>("rpps_form")
  const [finess, setFiness] = useState(localStorage.getItem(FINESS_KEY) ?? "")
  const [finessMessage, setFinessMessage] = useState<{ text: string; type: "error" | "success" } | null>(null)

  const professionalToInsert = useSelector(
    ({ carer }: { carer: Partial<InsertCarerStore> }) => carer.professionalToInsert,
  )
  const carerLoading = useSelector(
    ({ carer }: { carer: Partial<InsertCarerStore> }) => (carer as any).loading,
  )
  const carerMessage = useSelector(
    ({ carer }: { carer: Partial<InsertCarerStore> }) => (carer as any).message,
  )
  const adminLoading = useSelector(
    ({ admin }: { admin: Partial<AdminStore> }) => admin.loading,
  )
  const adminMessage = useSelector(
    ({ admin }: { admin: Partial<AdminStore> }) => admin.message,
  )

  useEffect(() => {
    dispatch({ type: CARERS_CLEAR_MESSAGE })
    return () => { dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT }) }
  }, [])

  useEffect(() => {
    if (professionalToInsert && step === "rpps_form") {
      setStep("rpps_confirm")
    }
  }, [professionalToInsert])

  useEffect(() => {
    if (step !== "finess" || adminMessage?.type !== "success" || !professionalToInsert?.rpps) return

    const storedFiness = localStorage.getItem(FINESS_KEY) ?? ""

    dispatch(
      actionsSurvey.setStepReplies({
        id: PATH.tle_pharmacist,
        replies: [
          {
            title: PATH.tle_pharmacist,
            value: {
              rpps: professionalToInsert.rpps,
              finess: storedFiness,
              name: [professionalToInsert.firstname, professionalToInsert.lastname]
                .filter(Boolean)
                .join(" "),
            },
          },
        ],
      }),
    )
    dispatch(actionsUpload.reset())
    localStorage.setItem("tle_session_start", new Date().toISOString())
    dispatch(actionsSurvey.next())
    navigate(SURVEY_PAGE)
  }, [adminMessage, step, professionalToInsert, dispatch, navigate])

  const handleFinessSubmit = () => {
    if (adminLoading || !professionalToInsert?.rpps) return
    const fiTrimmed = finess.trim()
    if (!/^\d{9}$/.test(fiTrimmed)) {
      setFinessMessage({ text: languages.pharmacistFinessLengthError, type: "error" })
      return
    }
    setFinessMessage(null)
    dispatch(
      adminBillingRequest({
        rpps: professionalToInsert.rpps,
        finess: fiTrimmed,
        owner_firstname: professionalToInsert.firstname || "",
        owner_lastname: professionalToInsert.lastname || "",
      }),
    )
  }

  const handlePrevious = () => {
    if (step === "finess") {
      setStep("rpps_confirm")
      return
    }
    if (step === "rpps_confirm") {
      dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
      setStep("rpps_form")
      return
    }
    dispatch({ type: CANCEL_PROFESSIONAL_TO_INSERT })
    dispatch(actionsSurvey.previous())
    navigate(SURVEY_PAGE)
  }

  const rppsInputs = [
    { name: "rpps", required: true, label: "", rules: [pharmacistRppsLengthValidator] },
  ]

  return (
    <>
      <Steps
        current={current}
        insideStep={insideStep}
        totalInsideSteps={totalInsideSteps}
        totalSteps={totalSteps}
      />
      <PreviousButton onClick={handlePrevious} />

      {step === "rpps_form" && (
        <Card message={carerMessage}>
          <FormPage
            title={
              <>
                <span style={{ fontWeight: 400 }}>{languages.pharmacistEnterRppsPrefix}{" "}</span>
                <strong>{languages.pharmacistTitulaireLabel}</strong>
              </>
            }
            inputs={rppsInputs}
            acceptText={languages.next}
            loading={carerLoading}
            onFinish={({ rpps }: { rpps: string }) =>
              dispatch({ type: GET_IDENTITY_FROM_RPPS_REQUEST, payload: { rpps } })
            }
            onCancel={handlePrevious}
          />
        </Card>
      )}

      {step === "rpps_confirm" && (
        <Card
          title={languages.pharmacistInfoTitle}
          style={{ height: "70vh", justifyContent: "center" }}
          message={carerMessage}
        >
          <div className="flex-column flex-center">
            <FatButton disabled>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "8px 0" }}>
                <img src={cdn("/images/ps_picto.svg")} alt="" />
                <b>
                  {professionalToInsert?.firstname} {professionalToInsert?.lastname}
                </b>
                <i>{professionalToInsert?.profession ?? languages.healthPro}</i>
                {professionalToInsert?.rpps && (
                  <span style={{ fontSize: 14, color: "#666" }}>
                    RPPS : {professionalToInsert.rpps}
                  </span>
                )}
              </div>
            </FatButton>
            <Buttons>
              <Button wide="long" onClick={handlePrevious}>
                {languages.cancel}
              </Button>
              <Button wide="long" type="primary" onClick={() => setStep("finess")}>
                {languages.pharmacistInfoContinue}
              </Button>
            </Buttons>
          </div>
        </Card>
      )}

      {step === "finess" && (
        <Card message={finessMessage ?? adminMessage}>
          <div style={{ fontSize: 65, fontWeight: 700, color: "#211452", lineHeight: "95px", textAlign: "center" }}>
            <strong>{languages.pharmacistFinessHeadingBold}</strong>{" "}
            <span style={{ fontWeight: 400 }}>{languages.pharmacistFinessHeadingSuffix}</span>
          </div>
          <Keyboard
            options={{ type: "finess", inputName: "finess" }}
            value={finess}
            onChange={(val: string) => {
              setFiness(val)
              setFinessMessage(null)
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 60, marginBottom: 8 }}>
              <div style={{ fontSize: 38, color: "#211452" }}>
                {languages.pharmacistFinessInputPrefix}{" "}
                <strong>{languages.pharmacyLabel}</strong>
              </div>
              <div style={{ position: "relative" }}>
                <Input
                  placeholder={languages.pharmacistFinessPlaceholder}
                  value={finess}
                  name="finess"
                />
                {finess ? (
                  <KeyboardReset
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setFiness("")
                      setFinessMessage(null)
                    }}
                  />
                ) : null}
              </div>
            </div>
          </Keyboard>

          <Buttons>
            <Button
              type="primary"
              onClick={handleFinessSubmit}
              disabled={!finess.trim() || adminLoading}
            >
              {languages.pharmacistInfoContinue}
            </Button>
          </Buttons>
        </Card>
      )}
    </>
  )
}

export default PharmacistInfo
